import { joinURL, withQuery } from 'ufo'

export const useStatamic = () => {
    const { $preview } = useNuxtApp()
    const apiUrl = joinURL(useRuntimeConfig().public.statamicUrl, '/api')

    return async (url, fetchOptions = {}) => {
        try {
            let endpoint = joinURL(apiUrl, url)

            if ($preview?.value) {
                // Add the preview token to the query string
                endpoint = withQuery(endpoint, {
                    token: $preview.value.token,
                    timestamp: Date.now(),
                })
            }

            fetchOptions.useAsyncData = fetchOptions.useAsyncData ?? true
            fetchOptions.method = fetchOptions.method || 'GET'

            const headers = {
                ...fetchOptions.headers,
                Accept: 'application/json',
            }

            // workaround for a Cloudflare bug
            const credentialsSupported = 'credentials' in Request.prototype

            // ensure we have an XSRF token if we're issuing anything other than a GET request
            if (fetchOptions.method !== 'GET') {
                // add the token to the request headers
                const xsrfToken = useCookie('XSRF-TOKEN')
                headers['X-XSRF-TOKEN'] = xsrfToken.value

                return $fetch(endpoint, {
                    ...fetchOptions,
                    credentials: credentialsSupported ? 'include' : undefined,
                    headers,
                })
            }

            const cacheKey = `${fetchOptions.method} ${endpoint}`

            return useAsyncData(
                cacheKey,
                async () =>
                    await $fetch(endpoint, {
                        ...fetchOptions,
                        credentials: credentialsSupported
                            ? 'include'
                            : undefined,
                        headers,
                    }),
                {
                    getCachedData: (key) => {
                        const cachedData = useNuxtData(key)

                        if (
                            fetchOptions.method !== 'GET' ||
                            !cachedData.data.value
                        ) {
                            // not a GET, or no cached data - don't use the cache
                            if (import.meta.dev) {
                                console.log(
                                    `[Statamic: ${fetchOptions.method}]`,
                                    endpoint,
                                )
                            }

                            return null
                        }

                        // check if there's cached data we can return
                        if (import.meta.dev) {
                            console.log(`[Statamic: CACHE HIT]`, endpoint)
                        }

                        return cachedData.data.value
                    },
                    transform: (data) => {
                        if (fetchOptions.transform) {
                            data = fetchOptions.transform(data)
                        }

                        // get rid of the "data" wrapper statamic adds
                        return data.data ? data.data : data
                    },
                },
            )
        } catch (error) {
            console.error('[Statamic: Error]', error)

            throw createError({
                statusCode: error.response?.status,
                statusMessage: error.response?.statusText,
                data: error.response?._data,
            })
        }
    }
}
